import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {Star, Heart, Share2, Truck, Shield, RotateCcw, Coffee, Thermometer, Clock} from 'lucide-react'
import ProductBreadcrumb from '../components/ProductBreadcrumb'
import AddToCartButton from '../components/AddToCartButton'
import { lumi } from '../lib/lumi'

// Define Product Interface matching DB schema
interface Product {
  _id: string
  productId: number
  name: string
  nameEn?: string
  price: number
  originalPrice?: number
  images: string[]
  description: string
  descriptionEn?: string
  origin?: string
  region?: string
  brewingMethods?: string[]
  flavorNotes?: string[]
  specifications?: {
    roast?: string
    variety?: string
    altitude?: string
    processing?: string
    weight?: string
    packaging?: string
  }
  inventory: number
  category: string
  rating?: number
  reviewCount?: number
  seoTitle?: string
  seoDescription?: string
  ogImage?: string
  // 訂閱屬性
  isSubscription?: boolean
  subscriptionMonths?: number
  subscriptionPrice?: number
  deliveryDayOptions?: number[]
}

const ProductPage: React.FC = () => {
  const { lang = 'zh', category, id } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [isWishlisted, setIsWishlisted] = useState(false)
  const [favoriteLoading, setFavoriteLoading] = useState(false)
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([])
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  // 訂閱相關狀態
  const [buyMode, setBuyMode] = useState<'single' | 'subscription'>('single')
  const [selectedDeliveryDay, setSelectedDeliveryDay] = useState<number>(10)
  const [subscriptionMonths, setSubscriptionMonths] = useState<number>(20)

  const currentLang = lang as 'zh' | 'en'

  // 自定义价格格式化函数，避免 toLocaleString 产生下标字符
  const formatPrice = (price: number) => {
    return Math.floor(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  useEffect(() => {
    if (id) {
      fetchProduct(id)
    }
  }, [id])

  useEffect(() => {
    checkIfFavorited()
  }, [product])

  useEffect(() => {
    if (product) {
      fetchRelatedProducts()
      injectProductSEO(product)
    }
  }, [product])

  useEffect(() => {
    // Cleanup SEO tags on unmount
    return () => {
      removeDynamicSEO()
    }
  }, [])

  const checkIfFavorited = async () => {
    if (!product) return
    try {
      const user = lumi.auth.user
      if (!user) {
        setIsWishlisted(false)
        return
      }
      const { list } = await lumi.entities.favorites.list({
        filter: { userEmail: user.email, productId: product._id },
        limit: 1
      })
      setIsWishlisted(list.length > 0)
    } catch (error) {
      console.error('Failed to check favorite status:', error)
      setIsWishlisted(false)
    }
  }

  const fetchProduct = async (productIdParam: string) => {
    setLoading(true)
    try {
      // Check if it looks like a valid ObjectId (24 hex chars)
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(productIdParam)
      
      // Convert to number if it's a productId (1001+), otherwise treat as _id
      const numericId = parseInt(productIdParam, 10)
      let result = null
      
      if (!isObjectId && !isNaN(numericId) && numericId >= 1001) {
        // Query by productId
        const response = await lumi.entities.products.list({
          filter: { productId: numericId },
          limit: 1
        })
        result = response.list?.[0] || null
      }
      
      // If no result found by productId (or if it was an ObjectId), try getting by _id
      if (!result) {
        // Fallback: query by _id (ObjectId)
        try {
          result = await lumi.entities.products.get(productIdParam)
        } catch (e) {
          // Ignore error if get by ID fails, it means product truly doesn't exist
          console.warn('Failed to fetch by _id fallback:', e)
        }
      }
      
      if (result) {
        setProduct(result as Product)
      } else {
        console.error('Product not found')
      }
    } catch (error) {
      console.error('Failed to fetch product:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchRelatedProducts = async () => {
    if (!product) return
    try {
      const { list } = await lumi.entities.products.list({
        filter: { 
          category: product.category,
          _id: { $ne: product._id }
        },
        limit: 4
      })
      setRelatedProducts(list as Product[])
    } catch (error) {
      console.error('Failed to fetch related products:', error)
    }
  }

  const generateAutoSEO = (product: Product) => {
    const productName = currentLang === 'zh' ? product.name : (product.nameEn || product.name)
    const productDesc = currentLang === 'zh' ? product.description : (product.descriptionEn || product.description)
    
    // 自動生成 SEO Title（依商品類別優化）
    let autoTitle = ''
    if (currentLang === 'zh') {
      const categoryMap: Record<string, string> = {
        'beans': '精品咖啡豆',
        'dripbag': '濾掛咖啡',
        'gift': '咖啡禮盒',
        'gear': '咖啡器具'
      }
      const categoryName = categoryMap[product.category] || '咖啡商品'
      autoTitle = `${productName}｜${categoryName}｜ONGU Studio 自家烘焙`
    } else {
      const categoryMap: Record<string, string> = {
        'beans': 'Specialty Coffee Beans',
        'dripbag': 'Drip Coffee',
        'gift': 'Coffee Gift Box',
        'gear': 'Coffee Equipment'
      }
      const categoryName = categoryMap[product.category] || 'Coffee'
      autoTitle = `${productName} | ${categoryName} | ONGU Studio`
    }

    // 自動生成 SEO Description（結合商品資訊）
    let autoDescription = ''
    if (currentLang === 'zh') {
      const specs = []
      if (product.origin) specs.push(product.origin)
      if (product.specifications?.roast) specs.push(product.specifications.roast)
      if (product.flavorNotes && product.flavorNotes.length > 0) {
        specs.push(`風味：${product.flavorNotes.slice(0, 3).join('、')}`)
      }
      const specsText = specs.length > 0 ? `${specs.join('｜')}。` : ''
      autoDescription = `ONGU Studio ${productName}，${specsText}${productDesc.substring(0, 120 - specsText.length)}...`
    } else {
      const specs = []
      if (product.origin) specs.push(product.origin)
      if (product.specifications?.roast) specs.push(product.specifications.roast)
      if (product.flavorNotes && product.flavorNotes.length > 0) {
        specs.push(`Notes: ${product.flavorNotes.slice(0, 3).join(', ')}`)
      }
      const specsText = specs.length > 0 ? `${specs.join(' | ')}. ` : ''
      autoDescription = `ONGU Studio ${productName}. ${specsText}${productDesc.substring(0, 140 - specsText.length)}...`
    }

    return { autoTitle, autoDescription }
  }

  const injectProductSEO = (product: Product) => {
    // Remove previous SEO tags
    removeDynamicSEO()

    const productName = currentLang === 'zh' ? product.name : (product.nameEn || product.name)
    const productDesc = currentLang === 'zh' ? product.description : (product.descriptionEn || product.description)
    const currentUrl = window.location.href
    const productImage = product.ogImage || product.images[0] || 'https://www.ongucoffee.com/og-default.jpg'

    // 自動生成 SEO（當無自訂值時使用）
    const { autoTitle, autoDescription } = generateAutoSEO(product)

    // 構建 Product Schema.org 結構化資料 (JSON-LD)
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': productName,
      'image': product.images.filter(Boolean),
      'description': productDesc,
      'brand': {
        '@type': 'Brand',
        'name': 'ONGU Studio'
      },
      'offers': {
        '@type': 'Offer',
        'url': currentUrl,
        'priceCurrency': 'TWD',
        'price': product.price.toString(),
        'priceValidUntil': new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        'availability': product.inventory > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        'itemCondition': 'https://schema.org/NewCondition',
        ...(product.originalPrice && product.originalPrice > product.price ? {
          'priceSpecification': {
            '@type': 'PriceSpecification',
            'price': product.originalPrice.toString(),
            'priceCurrency': 'TWD'
          }
        } : {})
      },
      'sku': product.productId?.toString() || product._id,
      'productID': product.productId?.toString() || product._id,
      'category': product.category,
      ...(product.rating && product.reviewCount ? {
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': product.rating.toString(),
          'reviewCount': product.reviewCount.toString()
        }
      } : {}),
      ...(product.specifications?.weight ? {
        'weight': {
          '@type': 'QuantitativeValue',
          'value': product.specifications.weight,
          'unitText': 'g'
        }
      } : {}),
      'additionalProperty': [
        ...(product.origin ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '產地' : 'Origin',
          'value': product.origin
        }] : []),
        ...(product.region ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '產區' : 'Region',
          'value': product.region
        }] : []),
        ...(product.specifications?.roast ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '烘焙度' : 'Roast Level',
          'value': product.specifications.roast
        }] : []),
        ...(product.specifications?.processing ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '處理法' : 'Processing',
          'value': product.specifications.processing
        }] : []),
        ...(product.specifications?.altitude ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '海拔' : 'Altitude',
          'value': product.specifications.altitude
        }] : []),
        ...(product.specifications?.variety ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '品種' : 'Variety',
          'value': product.specifications.variety
        }] : []),
        ...(product.flavorNotes && product.flavorNotes.length > 0 ? [{
          '@type': 'PropertyValue',
          'name': currentLang === 'zh' ? '風味筆記' : 'Flavor Notes',
          'value': product.flavorNotes.join(', ')
        }] : [])
      ].filter(Boolean)
    }

    // 注入 JSON-LD 結構化資料
    const scriptTag = document.createElement('script')
    scriptTag.type = 'application/ld+json'
    scriptTag.setAttribute('data-product-seo', 'true')
    scriptTag.textContent = JSON.stringify(productSchema)
    document.head.appendChild(scriptTag)

    // Meta Title - 移除所有舊的 title 標籤，確保新標題生效
    const title = product.seoTitle || autoTitle
    document.querySelectorAll('title').forEach(el => el.remove())
    const titleTag = document.createElement('title')
    titleTag.textContent = title
    titleTag.setAttribute('data-product-seo', 'true')
    document.head.appendChild(titleTag)

    // Meta Description - 移除所有舊的 description 標籤
    const description = product.seoDescription || autoDescription
    document.querySelectorAll('meta[name="description"]').forEach(el => el.remove())
    const metaDesc = document.createElement('meta')
    metaDesc.name = 'description'
    metaDesc.content = description
    metaDesc.setAttribute('data-product-seo', 'true')
    document.head.appendChild(metaDesc)

    // Open Graph Tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: productImage },
      { property: 'og:url', content: currentUrl },
      { property: 'og:type', content: 'product' },
      { property: 'product:price:amount', content: product.price.toString() },
      { property: 'product:price:currency', content: 'TWD' }
    ]

    ogTags.forEach(tag => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', tag.property)
      meta.content = tag.content
      meta.setAttribute('data-product-seo', 'true')
      document.head.appendChild(meta)
    })

    // Twitter Card Tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: productImage }
    ]

    twitterTags.forEach(tag => {
      const meta = document.createElement('meta')
      meta.name = tag.name
      meta.content = tag.content
      meta.setAttribute('data-product-seo', 'true')
      document.head.appendChild(meta)
    })

    console.log('✅ Product SEO injected:', productName)
  }

  const removeDynamicSEO = () => {
    document.querySelectorAll('[data-product-seo]').forEach(el => el.remove())
  }

  const handleAddToWishlist = async () => {
    if (!product) return
    
    const user = lumi.auth.user
    if (!user) {
      alert(currentLang === 'zh' ? '請先登入' : 'Please login first')
      return
    }

    setFavoriteLoading(true)
    try {
      if (isWishlisted) {
        // 移除收藏
        const { list } = await lumi.entities.favorites.list({
          filter: { userEmail: user.email, productId: product._id }
        })
        if (list.length > 0) {
          await lumi.entities.favorites.delete(list[0]._id)
        }
        setIsWishlisted(false)
        window.dispatchEvent(new Event('favoriteChanged'))
      } else {
        // 添加收藏
        await lumi.entities.favorites.create({
          userEmail: user.email,
          productId: product._id,
          productName: product.name,
          productImage: product.images[0] || '',
          productPrice: product.price,
          createdAt: new Date().toISOString()
        })
        setIsWishlisted(true)
        window.dispatchEvent(new Event('favoriteChanged'))
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
      alert(currentLang === 'zh' ? '操作失敗，請稍後再試' : 'Operation failed, please try again')
    } finally {
      setFavoriteLoading(false)
    }
  }

  const handleShare = async () => {
    if (!product) return
    
    const shareData = {
      title: currentLang === 'zh' ? product.name : (product.nameEn || product.name),
      text: currentLang === 'zh' ? product.description : (product.descriptionEn || product.description),
      url: window.location.href
    }

    try {
      // 优先使用 Web Share API（移动设备原生分享）
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        // 备选方案：复制链接到剪贴板
        await navigator.clipboard.writeText(window.location.href)
        alert(currentLang === 'zh' ? '連結已複製到剪貼板' : 'Link copied to clipboard')
      }
    } catch (error) {
      // 用户取消分享或复制失败
      if ((error as Error).name !== 'AbortError') {
        console.error('Share failed:', error)
        // 最终备选：手动提示用户复制
        alert(currentLang === 'zh' 
          ? `請手動複製此連結：${window.location.href}` 
          : `Please copy this link manually: ${window.location.href}`)
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zinc-400"></div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl text-zinc-900 dark:text-zinc-100 mb-4">找不到商品</h2>
          <button 
            onClick={() => navigate(`/${lang}/shop`)}
            className="px-6 py-2 bg-zinc-900 text-white rounded-sm"
          >
            返回商店
          </button>
        </div>
      </div>
    )
  }

  // 构建详细信息列表
  const details = [
    { label: currentLang === 'zh' ? '產地/產區' : 'ORIGIN', value: [product.origin, product.region].filter(Boolean).join(' / ') || '-' },
    { label: currentLang === 'zh' ? '海拔' : 'ALTITUDE', value: product.specifications?.altitude || '-' },
    { label: currentLang === 'zh' ? '處理法' : 'PROCESS', value: product.specifications?.processing || '-' },
    { label: currentLang === 'zh' ? '包裝' : 'PACKAGING', value: product.specifications?.packaging || (currentLang === 'zh' ? '單向透氣閥袋' : 'One-way valve bag') },
    { label: currentLang === 'zh' ? '保存期限' : 'SHELF LIFE', value: currentLang === 'zh' ? '12個月' : '12 months' },
    { label: currentLang === 'zh' ? '烘焙日期' : 'ROAST DATE', value: currentLang === 'zh' ? '出貨前3-7天' : '3-7 days before shipping' },
  ]

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 transition-colors duration-300">
      {/* 頂部導航 */}
      <ProductBreadcrumb 
        category={category} 
        productName={currentLang === 'zh' ? product.name : (product.nameEn || product.name)}
      />
      
      {/* 主要內容區 */}
      <div className="pt-32 pb-24">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* 左側 - 圖片 (佔 5 欄 = 40%) */}
            <div className="lg:col-span-5 space-y-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative aspect-square bg-zinc-100 dark:bg-zinc-900 overflow-hidden rounded-sm shadow-sm group cursor-pointer"
                onClick={() => product.images.length > 1 && setIsGalleryOpen(true)}
              >
                <img
                  src={product.images[0] || 'https://via.placeholder.com/800x1000?text=No+Image'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                />
                {product.images.length > 1 && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 dark:bg-zinc-900/90 px-4 py-2 rounded-sm">
                      <span className="text-xs tracking-widest uppercase text-zinc-900 dark:text-zinc-100">
                        {currentLang === 'zh' ? `查看全部 ${product.images.length} 張照片` : `VIEW ALL ${product.images.length} PHOTOS`}
                      </span>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* 圖片相簿集 Lightbox */}
            {isGalleryOpen && (
              <div 
                className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
                onClick={() => setIsGalleryOpen(false)}
              >
                <button
                  className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors p-2"
                  onClick={() => setIsGalleryOpen(false)}
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                    <img
                      src={product.images[selectedImage]}
                      alt={`${product.name} ${selectedImage + 1}`}
                      className="w-full h-auto max-h-[85vh] object-contain"
                    />
                    
                    {product.images.length > 1 && (
                      <>
                        <button
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors disabled:opacity-30"
                          onClick={() => setSelectedImage((prev) => (prev === 0 ? product.images.length - 1 : prev - 1))}
                          disabled={selectedImage === 0}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors disabled:opacity-30"
                          onClick={() => setSelectedImage((prev) => (prev === product.images.length - 1 ? 0 : prev + 1))}
                          disabled={selectedImage === product.images.length - 1}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                  
                  {product.images.length > 1 && (
                    <div className="mt-6 flex gap-3 justify-center overflow-x-auto pb-2">
                      {product.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`flex-shrink-0 w-20 h-20 rounded-sm overflow-hidden transition-all ${
                            selectedImage === index
                              ? 'ring-2 ring-white opacity-100'
                              : 'opacity-50 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 text-center text-white/60 text-sm tracking-wider">
                    {selectedImage + 1} / {product.images.length}
                  </div>
                </div>
              </div>
            )}

            {/* 右側 - 文字資訊 (佔 7 欄 = 60%) */}
            <div className="lg:col-span-7">
              <div className="sticky top-24 space-y-8">
                
                {/* 產品標題 */}
                <h1 className="text-3xl lg:text-4xl font-extralight tracking-wide text-zinc-900 dark:text-zinc-100 leading-tight">
                  {currentLang === 'zh' ? product.name : (product.nameEn || product.name)}
                </h1>

                {/* 烘焙度與風味 */}
                <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-300">
                  {product.specifications?.roast && (
                    <div>
                      <span className="mr-2 text-zinc-900 dark:text-zinc-100">{currentLang === 'zh' ? '烘焙度：' : 'ROAST: '}</span>
                      {product.specifications.roast}
                    </div>
                  )}
                  {product.flavorNotes && product.flavorNotes.length > 0 && (
                    <div>
                      <span className="mr-2 text-zinc-900 dark:text-zinc-100">{currentLang === 'zh' ? '風味筆記：' : 'FLAVOR NOTES: '}</span>
                      {product.flavorNotes.join(currentLang === 'zh' ? '、' : ', ')}
                    </div>
                  )}
                </div>

                {/* 重量與價格 */}
                <div className="space-y-2">
                  <div className="text-sm text-zinc-600 dark:text-zinc-300">
                    <span className="mr-2 text-zinc-900 dark:text-zinc-100">{currentLang === 'zh' ? '重量：' : 'WEIGHT: '}</span>
                    {product.specifications?.weight || '227g'}
                  </div>
                  <div className="flex items-baseline space-x-4">
                    <span className="text-2xl font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                      NT$ {formatPrice(Number(product.price))}
                    </span>
                    {product.originalPrice && product.originalPrice > product.price && (
                      <span className="text-base text-zinc-400 line-through font-extralight">
                        NT$ {formatPrice(Number(product.originalPrice))}
                      </span>
                    )}
                  </div>
                </div>

                {/* 詳細規格列表 */}
                <div className="grid grid-cols-1 gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                  {details.map((detail, index) => (
                    <div key={index} className="flex">
                      <span className="mr-2 text-zinc-900 dark:text-zinc-100 min-w-[5em]">
                        {detail.label}：
                      </span>
                      <span>{detail.value}</span>
                    </div>
                  ))}
                </div>

                {/* 訂閱購買 UI（僅當商品支援訂閱時顯示） */}
                {product.isSubscription && (
                  <div className="border border-zinc-200 dark:border-zinc-700 rounded-sm overflow-hidden">
                    {/* 模式切換 */}
                    <div className="grid grid-cols-2 text-xs tracking-widest">
                      <button
                        onClick={() => setBuyMode('single')}
                        className={`py-2.5 transition-colors ${
                          buyMode === 'single'
                            ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
                            : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                        }`}
                      >
                        {currentLang === 'zh' ? '單次購買' : 'ONE-TIME'}
                      </button>
                      <button
                        onClick={() => setBuyMode('subscription')}
                        className={`py-2.5 transition-colors ${
                          buyMode === 'subscription'
                            ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
                            : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                        }`}
                      >
                        {currentLang === 'zh' ? '訂閱方案' : 'SUBSCRIBE'}
                      </button>
                    </div>

                    {/* 訂閱設定面板 */}
                    {buyMode === 'subscription' && (
                      <div className="p-4 space-y-4 bg-zinc-50 dark:bg-zinc-800/50">
                        {/* 訂閱月數 */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-500 dark:text-zinc-400">
                            {currentLang === 'zh' ? '訂閱方案' : 'Plan'}
                          </span>
                          <select
                            value={subscriptionMonths}
                            onChange={e => setSubscriptionMonths(Number(e.target.value))}
                            className="text-sm font-light text-zinc-900 dark:text-zinc-100 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 focus:outline-none"
                          >
                            {[6, 12, 20].map(m => (
                              <option key={m} value={m}>{m} {currentLang === 'zh' ? '個月' : 'months'}</option>
                            ))}
                          </select>
                        </div>

                        {/* 每月出貨日 */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-500 dark:text-zinc-400">
                            {currentLang === 'zh' ? '每月出貨日' : 'Delivery Day'}
                          </span>
                          <select
                            value={selectedDeliveryDay}
                            onChange={e => setSelectedDeliveryDay(Number(e.target.value))}
                            className="text-sm font-light text-zinc-900 dark:text-zinc-100 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 focus:outline-none"
                          >
                            {(product.deliveryDayOptions || [1, 5, 10, 15, 20, 25]).map(d => (
                              <option key={d} value={d}>{currentLang === 'zh' ? `每月 ${d} 日` : `${d}th of month`}</option>
                            ))}
                          </select>
                        </div>

                        {/* 金額說明 */}
                        <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 space-y-1">
                          <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
                            <span>{currentLang === 'zh' ? '月單價' : 'Monthly'}</span>
                            <span>NT$ {formatPrice(Number(product.price))}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm font-light text-zinc-900 dark:text-zinc-100">
                            <span>{currentLang === 'zh' ? `一次預收（${subscriptionMonths}個月）` : `Total (${subscriptionMonths}mo)`}</span>
                            <span className="text-base">
                              NT$ {formatPrice((product.subscriptionPrice || product.price) * subscriptionMonths)}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 font-light">
                          {currentLang === 'zh'
                            ? `每月 ${selectedDeliveryDay} 日出貨，共 ${subscriptionMonths} 個月，一次付清。`
                            : `Ships on the ${selectedDeliveryDay}th each month for ${subscriptionMonths} months, paid upfront.`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* 購買按鈕區 */}
                <div className="space-y-3 pt-2">
                  {buyMode === 'subscription' && product.isSubscription ? (
                    <button
                      onClick={() => alert(currentLang === 'zh' ? '訂閱功能即將開放，敬請期待！' : 'Subscription coming soon!')}
                      className="w-full h-12 bg-emerald-700 hover:bg-emerald-600 text-white text-sm tracking-widest uppercase transition-colors rounded-sm"
                    >
                      {currentLang === 'zh' ? `立即訂閱 · ${subscriptionMonths} 個月` : `SUBSCRIBE · ${subscriptionMonths} MO`}
                    </button>
                  ) : (
                  <AddToCartButton
                    product={product}
                    quantity={quantity}
                    showQuantity={true}
                    size="lg"
                    variant="primary"
                    className="w-full rounded-sm h-12 text-sm tracking-widest uppercase"
                  />
                  )}
                  
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleAddToWishlist}
                      disabled={favoriteLoading}
                      className={`flex-1 py-3 flex items-center justify-center space-x-2 border rounded-sm transition-colors duration-300 disabled:opacity-50 ${
                        isWishlisted
                          ? 'border-red-500 text-red-500 bg-red-50 dark:bg-red-900/10'
                          : 'border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      }`}
                    >
                      {favoriteLoading ? (
                        <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin"></div>
                      ) : (
                        <Heart size={18} className={isWishlisted ? 'fill-current' : ''} />
                      )}
                      <span className="text-xs tracking-widest uppercase">{currentLang === 'zh' ? (isWishlisted ? '已收藏' : '加入收藏') : (isWishlisted ? 'FAVORITED' : 'WISHLIST')}</span>
                    </button>
                    <button 
                      onClick={handleShare}
                      className="flex-1 py-3 flex items-center justify-center space-x-2 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 rounded-sm hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors duration-300"
                    >
                      <Share2 size={18} />
                      <span className="text-xs tracking-widest uppercase">{currentLang === 'zh' ? '分享' : 'SHARE'}</span>
                    </button>
                  </div>

                {/* 產品故事 */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold tracking-[0.2em] text-zinc-900 dark:text-zinc-100 uppercase">
                    {currentLang === 'zh' ? '產品故事' : 'STORY'}
                  </h3>
                  <div className="prose dark:prose-invert max-w-none">
                    <p className="text-zinc-600 dark:text-zinc-300 text-base leading-relaxed font-light whitespace-pre-line">
                      {currentLang === 'zh' ? product.description : (product.descriptionEn || product.description)}
                    </p>
                  </div>
                </div>

                {/* 分隔線 */}
                <div className="border-t border-zinc-200 dark:border-zinc-800" />

                {/* 沖煮建議 */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold tracking-[0.2em] text-zinc-900 dark:text-zinc-100 uppercase">
                    {currentLang === 'zh' ? '沖煮建議' : 'BREWING GUIDE'}
                  </h3>
                  
                  {product.brewingMethods && product.brewingMethods.length > 0 ? (
                    <div className="space-y-4 text-sm text-zinc-600 dark:text-zinc-300 font-light">
                      <div className="grid grid-cols-2 gap-4">
                         <div>
                            <div className="text-zinc-900 dark:text-zinc-100 mb-1">{currentLang === 'zh' ? '水溫' : 'TEMP'}</div>
                            <div>90-92°C</div>
                         </div>
                         <div>
                            <div className="text-zinc-900 dark:text-zinc-100 mb-1">{currentLang === 'zh' ? '粉水比' : 'RATIO'}</div>
                            <div>1:15</div>
                         </div>
                         <div>
                            <div className="text-zinc-900 dark:text-zinc-100 mb-1">{currentLang === 'zh' ? '研磨度' : 'GRIND'}</div>
                            <div>{currentLang === 'zh' ? '中細' : 'Medium-Fine'}</div>
                         </div>
                         <div>
                            <div className="text-zinc-900 dark:text-zinc-100 mb-1">{currentLang === 'zh' ? '適合器具' : 'TOOLS'}</div>
                            <div>{product.brewingMethods.join(' / ')}</div>
                         </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-500 italic">
                      {currentLang === 'zh' ? '暫無特定沖煮建議' : 'No specific brewing guide available'}
                    </div>
                  )}
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* 您可能也喜歡 - 推薦商品區塊 */}
        {relatedProducts.length > 0 && (
          <div className="mt-24 pt-24 border-t border-zinc-200 dark:border-zinc-800 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
            <h2 className="text-2xl font-light text-zinc-900 dark:text-zinc-100 mb-12 tracking-wide">
              {currentLang === 'zh' ? '您可能也喜歡' : 'YOU MAY ALSO LIKE'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {relatedProducts.map((relatedProduct) => (
                <motion.div
                  key={relatedProduct._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors duration-300 hover:shadow-lg flex flex-col"
                >
                  {/* 商品圖片 */}
                  <div 
                    className="relative overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/${lang}/shop/${relatedProduct.category}/${relatedProduct.productId || relatedProduct._id}/${relatedProduct.name}`)}
                  >
                    <img
                      src={relatedProduct.images[0] || 'https://via.placeholder.com/400x500'}
                      alt={relatedProduct.name}
                      className="w-full aspect-square object-contain group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                  </div>
                  
                  {/* 商品資訊 - 統一三層格式：Title / 價格 / Add To Cart */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <h3 
                        className="text-sm font-light text-gray-800 dark:text-gray-200 flex-1 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        onClick={() => navigate(`/${lang}/shop/${relatedProduct.category}/${relatedProduct.productId || relatedProduct._id}/${relatedProduct.name}`)}
                      >
                        {currentLang === 'zh' ? relatedProduct.name : (relatedProduct.nameEn || relatedProduct.name)}
                      </h3>
                      <div className="text-sm font-light text-gray-900 dark:text-gray-100 ml-4">
                        NT$ {formatPrice(Number(relatedProduct.price))}
                      </div>
                    </div>
                    
                    {/* 加入購物車按鈕 */}
                    <div 
                      className="mt-auto"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      <AddToCartButton
                        product={relatedProduct}
                        size="sm"
                        variant="secondary"
                        className="w-full"
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductPage